'use client';

import { useState, useCallback } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { SCREENS } from './WhyVoleyModal.screens';

interface WhyVoleyModalProps {
  onFinish: () => void;
}

export function WhyVoleyModal({ onFinish }: WhyVoleyModalProps) {
  const [activeScreen, setActiveScreen] = useState(0);
  const isLastScreen = activeScreen === SCREENS.length - 1;

  const handleNext = useCallback(() => {
    if (isLastScreen) {
      onFinish();
    } else {
      setActiveScreen((prev) => prev + 1);
    }
  }, [isLastScreen, onFinish]);

  const handleBack = useCallback(() => {
    setActiveScreen((prev) => Math.max(0, prev - 1));
  }, []);

  const screen = SCREENS[activeScreen];
  const Icon = screen.icon;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" />

      {/* Modal */}
      <div className="fixed inset-0 z-[201] flex items-center justify-center p-4">
        <div
          className="w-full max-w-xl rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 fade-in duration-300"
          role="dialog"
          aria-modal="true"
          aria-label="Why Voley"
        >
          <div className="p-8">
            {/* Icon and Heading */}
            <div className="flex items-start gap-4 mb-5">
              <div className={`flex items-center justify-center h-12 w-12 rounded-xl shrink-0 ${screen.iconBg}`}>
                <Icon className={`h-6 w-6 ${screen.iconColor}`} />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900">{screen.heading}</h2>
                {screen.subtitle && (
                  <p className="text-sm text-gray-500 mt-0.5">{screen.subtitle}</p>
                )}
              </div>
            </div>

            {/* Body */}
            <div className="text-left leading-relaxed space-y-3 text-gray-600 ml-16 mb-8">
              {screen.body}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              {activeScreen > 0 ? (
                <button
                  onClick={handleBack}
                  aria-label="Previous screen"
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </button>
              ) : (
                <div />
              )}

              <button
                onClick={handleNext}
                aria-label={isLastScreen ? 'Finish' : 'Next screen'}
                className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full text-sm font-medium text-white bg-accent-blue hover:brightness-110 transition-all shadow-sm hover:shadow-md"
              >
                {isLastScreen ? 'Get Started' : 'Next'}
                {!isLastScreen && <ChevronRight className="h-4 w-4" />}
              </button>
            </div>

            {/* Step dots */}
            <div className="flex justify-center gap-1.5 mt-5">
              {SCREENS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-200 ${
                    i === activeScreen ? 'w-6 bg-accent-blue' : 'w-1.5 bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
